/**
 * https://dw-rnd.atlassian.net/wiki/x/BwDJQ
 */

import AccountId from "components/atoms/text/labels/AccountId";
import * as s from "./AccountAccessStyled";
import SmallPopupTemplate from "components/templates/modal/small/SmallPopupTemplate";
import { FaUser } from "react-icons/fa";
import { MdOutlineAccessTimeFilled } from "react-icons/md";
import { RiShieldCheckFill } from "react-icons/ri";
import {
  ProfileAccountInfo,
  TEMPORARY_ACCESS_STATUS,
} from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import SelectComponent from "components/atoms/select/SelectComponent";
import { accessDurationOptions, Option } from "utils/options";
import { ACCESS_TYPE, isAccessTypeDisabled } from "./AccountAccessUtil";
import { RequestTemporaryBody } from "api/interfaces/userInterface.interface";
import { ValidateWord } from "components/atoms/text/Text";
import { useMemo } from "react";

type Props = {
  formatAccount: ProfileAccountInfo;
  selectedAccessType: ACCESS_TYPE;
  onModal: () => void;
  onChangeAccessType: (value: ACCESS_TYPE) => void;
  adminUserOptions: Option[];
  requestInfo: RequestTemporaryBody | null;
  onChangeRecipient: ({
    name,
    value,
  }: {
    name: string;
    value: string | number;
  }) => void;
  onClickOk: () => void;
  validateMsg: Record<string, string>;
  useTemporary: boolean;
  isShowOnlyUser: boolean;
  adminUserIsFetching: boolean;
};

const accessList = [
  {
    title: "Site Health Viewer",
    content:
      "Access the organization with viewer privileges limited to viewing configuration and overall system health at a glance.",
    icon: <RiShieldCheckFill />,
    value: ACCESS_TYPE.MANAGED,
  },
  {
    title: "Login as Registered Member",
    content:
      "Continue using your existing account associated with this organization.",
    icon: <FaUser />,
    value: ACCESS_TYPE.REGISTERED,
  },
  {
    title: "Request Temporary Access",
    content:
      "Send a request to the End-User Admin for temporary access. Once approved, you can access the organization for a limited duration to perform specific tasks.",
    icon: <MdOutlineAccessTimeFilled />,
    value: ACCESS_TYPE.TEMPORARY,
  },
];

export default function AccountAccessPresenter({
  useTemporary,
  isShowOnlyUser,
  formatAccount,
  selectedAccessType,
  onModal,
  onChangeAccessType,
  adminUserOptions,
  requestInfo,
  onChangeRecipient,
  onClickOk,
  validateMsg,
  adminUserIsFetching,
}: Props): JSX.Element {
  const title = (
    <s.ModalTitle>
      <AccountId
        level={formatAccount.accountLevel}
        accountId={formatAccount.accountNumber}
      />

      {formatAccount.accountName}
      <div className="sub">Select tour preferred access method to continue</div>
    </s.ModalTitle>
  );

  const stopEvent = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };
  const list = useMemo(() => {
    return accessList.filter((access) => {
      const isTemporary = access.value === ACCESS_TYPE.TEMPORARY;
      if (!useTemporary && access.value === ACCESS_TYPE.TEMPORARY) {
        return false;
      }
      if (
        isTemporary &&
        //  Temporary 안보이는 조건

        (formatAccount.accountLevel !== LEVEL_TYPE.EU ||
          formatAccount.isAdminGroup ||
          formatAccount.isTemporaryAccess === TEMPORARY_ACCESS_STATUS.ACC)
      ) {
        return false;
      }
      if (isShowOnlyUser && isAccessTypeDisabled(formatAccount, access.value)) {
        return false;
      }
      return true;
    });
  }, [formatAccount, useTemporary]);

  return (
    <SmallPopupTemplate
      title={title}
      onClick={onClickOk}
      onModal={onModal}
      cancel
      data-testid="account-access-small-popup-template-click-ok"
    >
      <s.AccessList>
        {list.map((access) => {
          const isTemporary = access.value === ACCESS_TYPE.TEMPORARY;

          const isDisabled = isAccessTypeDisabled(formatAccount, access.value);
          const isSelected = selectedAccessType === access.value;

          return (
            <s.AccessItem
              as="div"
              key={access.value}
              isDisabled={isDisabled}
              checked={isSelected}
              onClick={() => {
                !isDisabled && onChangeAccessType(access.value);
              }}
              data-testid="account-access-item-change-access-type"
            >
              <span className="radio-item-checked-icon">{access.icon}</span>
              <div className="report-item-content">
                <div className="report-item-title">
                  <div className="report-radio-item">{access.title}</div>
                  <div className="radio-wrapper">
                    {access.value === ACCESS_TYPE.REGISTERED &&
                      formatAccount.isTemporaryAccess ===
                        TEMPORARY_ACCESS_STATUS.ACC && (
                        <span className="label temporary-label">
                          TEMPORARY ACCESS
                        </span>
                      )}

                    <input
                      type="radio"
                      name="accessType"
                      value={access.value}
                      checked={isSelected}
                      onChange={() => onChangeAccessType(access.value)}
                      disabled={isDisabled}
                      data-testid={`account-access-type-radio-${access.value}`}
                    />
                  </div>
                </div>
                <div className="subtitle">{access.content}</div>

                {isTemporary && (
                  <>
                    {/* temporary 진행중일 땐 진행중 표시 */}
                    {formatAccount.isTemporaryAccess ===
                      TEMPORARY_ACCESS_STATUS.REQ && (
                      <div className="subtitle temporary-requset-info">
                        Temporary access request is pending approval.
                      </div>
                    )}

                    <div
                      className="temporary-button-wrapper"
                      onMouseDown={stopEvent}
                      onClick={stopEvent}
                      data-testid="account-access-div-stop-event"
                    >
                      <div>
                        <span className="subtitle">Recipient Admin</span>
                        <SelectComponent
                          options={adminUserOptions}
                          value={
                            adminUserOptions.find(
                              (opt) => opt.value === requestInfo?.recipient
                            ) ?? undefined
                          }
                          onChange={(value: string) => {
                            onChangeRecipient({ name: "recipient", value });
                          }}
                          loading={adminUserIsFetching}
                          data-testid="account-access-selects-change-recipient"
                        />
                      </div>
                      <div>
                        <span className="subtitle">Duration</span>
                        <SelectComponent
                          options={accessDurationOptions}
                          value={
                            accessDurationOptions.find(
                              (opt) =>
                                opt.value === String(requestInfo?.duration)
                            ) ?? undefined
                          }
                          onChange={(value: string) => {
                            onChangeRecipient({
                              name: "duration",
                              value: Number(value),
                            });
                          }}
                          data-testid="account-access-selects-change-recipient-2"
                        />
                      </div>
                    </div>
                    {validateMsg.recipient && (
                      <ValidateWord data-testid="account-access-validate-recipient">
                        {validateMsg.recipient}
                      </ValidateWord>
                    )}

                    {validateMsg.duration && (
                      <ValidateWord data-testid="account-access-validate-duration">
                        {validateMsg.duration}
                      </ValidateWord>
                    )}
                  </>
                )}
              </div>
            </s.AccessItem>
          );
        })}
      </s.AccessList>
    </SmallPopupTemplate>
  );
}
